import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';
import { toast } from 'sonner';
import { DarkBlueLogo } from '@/components/brand';
import {
    ArrowLeft,
    Floppy,
    ColorSwatch,
    Element4,
    ArrowDown2,
    MessageText1,
    Clock,
    Warning2,
} from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import UpgradeAccountButton from '@/components/subscription/UpgradeAccountButton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { ReportEditorAction } from '@/lib/analytics-events';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture, captureFeatureBlocked } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { PatientDetailPage } from '@/pages/constants';
import { canExportReport } from '@/utils';
import { formatUpdatedAt } from '@/utils/formatUpdatedAt';
import { useReportWizard } from '../context/ReportWizardContext';
import { ToolbarAction } from './ToolbarAction';

interface HeaderProps {
    activeCommentCount?: number;
    onOpenNewComment: () => void;
    onOpenAllComments: () => void;
}

export const Header: React.FC<HeaderProps> = ({
    activeCommentCount = 0,
    onOpenNewComment,
    onOpenAllComments,
}) => {
    const {
        formData,
        handleBack,
        handleSave,
        isSaving,
        saveFailure,
        handleComplete,
        handleSaveTemplate,
        handleOpenTemplates,
        requestReportImport,
        setShowCustomizeModal,
        isReadOnly,
        canSaveReport,
        handleExport,
    } = useReportWizard();
    const reportImportFeature = useFeatureFlag('reportImports.docxImport');

    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    const { hasSubscription, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
    const saveTemplateAccess = useFeatureEntitlement('reportTemplates.custom');
    const createReportAccess = useFeatureEntitlement('reports.create');

    const testsPerformedAtStart = formData.tests_performed_at_start;
    const patientId = formData.patient?.id;
    const patientName = formData.patient?.full_name || 'Novo Laudo';
    const reportYear = testsPerformedAtStart
        ? format(new Date(testsPerformedAtStart), 'yyyy')
        : new Date().getFullYear();
    const exportEnabled = canExportReport(formData);
    const updatedAtLabel = isSaving ? 'Salvando…' : formatUpdatedAt(formData.updated_at || '');
    const createReportBlockedPlan =
        !formData.id && createReportAccess.shouldBlock
            ? createReportAccess.availableFromPlan
            : null;
    const createReportBlockedMessage = getEntitlementBlockedMessage(createReportAccess.decision);

    // `canSaveReport` is false for two different reasons, and the user deserves
    // to be told which one applies to them.
    const saveBlockedReason = canSaveReport
        ? null
        : isReadOnly
          ? 'Este laudo está em modo somente leitura.'
          : 'Escolha o paciente para salvar o laudo.';
    const primaryBlockedReason = isReadOnly
        ? exportEnabled
            ? null
            : 'Conclua o laudo para exportá-lo.'
        : formData.id || patientId
          ? null
          : 'Escolha o paciente para concluir o laudo.';

    // Tracked here rather than inside the context handlers: `handleSave` is
    // shared with Ctrl+S, so instrumenting it there would report keyboard use
    // as toolbar use.
    const trackAction = (action: ReportEditorAction) => {
        capture('report_editor_action_clicked', { action, is_read_only: isReadOnly });
    };

    const handlePrimaryAction = () => {
        if (isReadOnly) {
            trackAction('export');
            handleExport();
            return;
        }

        trackAction('conclude');
        handleComplete();
    };

    return (
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-card px-3 py-2 md:flex-nowrap md:px-6 md:py-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                        trackAction('back');
                        void handleBack();
                    }}
                    className="size-9 flex-shrink-0 rounded-lg border border-border hover:bg-surface-muted"
                >
                    <ArrowLeft className="size-4" />
                </Button>
                <div className="hidden size-10 flex-shrink-0 items-center justify-center sm:flex">
                    <DarkBlueLogo />
                </div>
                <div className="min-w-0">
                    <h1 className="truncate text-sm font-semibold text-foreground sm:text-base">
                        {patientId ? (
                            <Link
                                to={`/${generatePath(PatientDetailPage, { patientId })}`}
                                className="cursor-pointer text-foreground no-underline hover:text-foreground hover:no-underline"
                            >
                                {patientName}
                            </Link>
                        ) : (
                            patientName
                        )}{' '}
                        - {reportYear}
                    </h1>
                </div>
            </div>
            <TooltipProvider delayDuration={0}>
                <div className="order-3 flex w-full flex-nowrap items-center justify-start gap-1.5 overflow-x-auto pb-1 md:order-none md:w-auto md:justify-end md:gap-3 md:overflow-visible md:pb-0">
                    {saveFailure ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        trackAction('save');
                                        void handleSave();
                                    }}
                                    aria-label="Alterações não salvas. Tentar salvar de novo"
                                    className="h-9 flex-shrink-0 gap-1.5 rounded-lg px-2 text-xs font-medium text-destructive hover:bg-destructive/10 hover:text-destructive"
                                >
                                    <Warning2 className="size-4" />
                                    <span className="hidden min-[1025px]:inline">
                                        Alterações não salvas
                                    </span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs">
                                {saveFailure}
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <span className="hidden text-xs text-muted-foreground min-[1025px]:inline">
                            {updatedAtLabel}
                        </span>
                    )}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                tabIndex={0}
                                className="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[1025px]:hidden"
                                aria-label={updatedAtLabel}
                            >
                                <Clock className="size-4" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{updatedAtLabel}</TooltipContent>
                    </Tooltip>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                        <ToolbarAction
                            variant="surface"
                            size="icon"
                            id="tour-save-btn"
                            aria-label="Salvar laudo"
                            className={cn(
                                'size-9 rounded-lg text-muted-foreground',
                                // The variant's hover background would otherwise still
                                // light up under a cursor that cannot click.
                                saveBlockedReason && 'hover:bg-card',
                            )}
                            onClick={() => {
                                trackAction('save');
                                void handleSave();
                            }}
                            blockedReason={saveBlockedReason}
                            tooltip="Salvar laudo"
                            reasonId="report-save-blocked-reason"
                        >
                            <Floppy className="size-4" />
                        </ToolbarAction>
                        {createReportBlockedPlan ? (
                            <FeatureUnavailableChip
                                availablePlan={createReportBlockedPlan}
                                message={createReportBlockedMessage}
                                side="bottom"
                            />
                        ) : null}
                    </div>
                    <DropdownMenu>
                        <Tooltip>
                            <DropdownMenuTrigger asChild>
                                <TooltipTrigger asChild>
                                    <Button
                                        id="tour-comments-btn"
                                        variant="surface"
                                        size="icon"
                                        className="relative size-9 rounded-lg text-muted-foreground"
                                        aria-label="Comentários"
                                    >
                                        <MessageText1 className="size-4" />
                                        {activeCommentCount > 0 && (
                                            <Badge
                                                variant="brand"
                                                className="absolute -right-1 -top-1 min-w-5 justify-center rounded-full px-1 py-0 text-[10px] leading-4"
                                            >
                                                {activeCommentCount}
                                            </Badge>
                                        )}
                                    </Button>
                                </TooltipTrigger>
                            </DropdownMenuTrigger>
                            <TooltipContent side="bottom">Comentários</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end" className="min-w-[220px]">
                            <DropdownMenuItem
                                onClick={() => {
                                    trackAction('comments_all');
                                    onOpenAllComments();
                                }}
                                className="cursor-pointer"
                            >
                                Ver todos os comentários
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    trackAction('comments_new');
                                    onOpenNewComment();
                                }}
                                className="cursor-pointer"
                            >
                                Novo comentário
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                id="tour-customize-btn"
                                variant="outline"
                                onClick={() => {
                                    trackAction('customize');
                                    setShowCustomizeModal(true);
                                }}
                                disabled={isReadOnly}
                                className="size-9 gap-0 rounded-lg border-border px-0 hover:bg-surface-muted min-[1025px]:w-auto min-[1025px]:gap-2 min-[1025px]:px-4"
                                aria-label="Personalizar"
                            >
                                <ColorSwatch className="size-4" />
                                <span className="hidden min-[1025px]:inline">Personalizar</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="min-[1025px]:hidden">
                            Personalizar
                        </TooltipContent>
                    </Tooltip>

                    <DropdownMenu>
                        <Tooltip>
                            <DropdownMenuTrigger asChild>
                                <TooltipTrigger asChild>
                                    <Button
                                        id="tour-save-template-btn"
                                        variant="outline"
                                        className="size-9 gap-0 rounded-lg border-border px-0 hover:bg-surface-muted min-[1025px]:w-auto min-[1025px]:gap-2 min-[1025px]:px-4"
                                        aria-label="Template"
                                    >
                                        <Element4 className="size-4" />
                                        <span className="hidden min-[1025px]:inline">Template</span>
                                        <ArrowDown2 className="hidden size-3 text-muted-foreground min-[1025px]:block" />
                                    </Button>
                                </TooltipTrigger>
                            </DropdownMenuTrigger>
                            <TooltipContent side="bottom" className="min-[1025px]:hidden">
                                Template
                            </TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end" className="min-w-[200px]">
                            <DropdownMenuItem
                                onSelect={(event: Event) => {
                                    trackAction('template_save');

                                    if (saveTemplateAccess.shouldBlock) {
                                        event.preventDefault();
                                        captureFeatureBlocked(
                                            'report_templates',
                                            saveTemplateAccess.decision,
                                        );
                                        toast.warning(
                                            getEntitlementBlockedMessage(
                                                saveTemplateAccess.decision,
                                            ),
                                        );
                                        return;
                                    }

                                    handleSaveTemplate();
                                }}
                                aria-disabled={saveTemplateAccess.shouldBlock}
                                className={cn(
                                    'cursor-pointer justify-between gap-3',
                                    saveTemplateAccess.shouldBlock &&
                                        'cursor-default text-muted-foreground focus:text-muted-foreground',
                                )}
                            >
                                <span>Salvar como template</span>
                                {saveTemplateAccess.isUnavailableInPlan &&
                                saveTemplateAccess.availableFromPlan ? (
                                    <FeatureUnavailableChip
                                        availablePlan={saveTemplateAccess.availableFromPlan}
                                        side="left"
                                    />
                                ) : null}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    trackAction('template_list');
                                    void handleOpenTemplates();
                                }}
                                className="cursor-pointer"
                                disabled={isReadOnly}
                            >
                                Meus templates
                            </DropdownMenuItem>
                            {reportImportFeature.enabled && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        trackAction('report_import');
                                        requestReportImport();
                                    }}
                                    className="cursor-pointer"
                                    disabled={isReadOnly}
                                >
                                    Importar de um laudo (.docx)
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {!isSubscriptionLoading && !hasSubscription && (
                        <>
                            <UpgradeAccountButton iconOnly className="rounded-lg md:hidden" />
                            <UpgradeAccountButton className="hidden rounded-lg md:inline-flex" />
                        </>
                    )}
                    <ToolbarAction
                        id="tour-export-btn"
                        onClick={handlePrimaryAction}
                        className={cn(
                            'h-9 flex-shrink-0 rounded-lg px-3 text-xs md:h-10 md:px-4 md:text-sm',
                            primaryBlockedReason && 'hover:bg-primary',
                        )}
                        blockedReason={primaryBlockedReason}
                        reasonId="report-primary-action-blocked-reason"
                    >
                        {isReadOnly ? (
                            <>
                                <span className="hidden sm:inline">Exportar Laudo</span>
                                <span className="sm:hidden">Exportar</span>
                            </>
                        ) : (
                            <>
                                <span className="hidden sm:inline">Concluir laudo</span>
                                <span className="sm:hidden">Concluir</span>
                            </>
                        )}
                    </ToolbarAction>
                </div>
            </TooltipProvider>
        </div>
    );
};
